import type { ReactNode } from 'react'
import { ChevronDownIcon } from '@/components/Icons'

type CopilotPillSelectProps = {
  /** Announced to a screen reader; the pill shows `display` instead. */
  label: string
  /** What the pill reads as right now — a project key, a model name. */
  display: string
  value: string
  onChange: (value: string) => void
  /** `<option>` list. */
  children: ReactNode
  /** Leading mark: a status dot, a model's bolt. */
  icon?: ReactNode
  /** Warm border and warm fill, for a pill that is asking to be used. */
  tone?: 'neutral' | 'attention'
  disabled?: boolean
  testId?: string
  title?: string
}

const TONE_CLASS = {
  neutral:
    'border-[#e2e8f0] text-[#64748b] hover:bg-slate-50 dark:border-[var(--border-color)] dark:text-[var(--text-secondary)] dark:hover:bg-white/5',
  attention:
    'border-[#f59e0b] bg-[#fcf9ef] text-[#b45309] hover:bg-[#fdf6e3] dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-400',
} as const

/**
 * A dropdown wearing the Copilot filter row's chip (BRAI-712's `FilterChip`),
 * so what the panel is set to reads as part of the same row as what it is
 * searching rather than as a form below it.
 *
 * Still a native `select`, and the transparent one laid over the chip is how:
 * below `lg` this panel is a sheet that slides in on a transform, and a
 * transformed ancestor is what `position: fixed` resolves against — a
 * JavaScript dropdown places its panel in viewport coordinates and lands a
 * whole sidebar-width off the screen. A native one is drawn outside the page's
 * layout altogether, which is the one thing that cannot catch.
 *
 * The visible text is `display` rather than the selected `<option>`, because
 * the option has to say enough to choose between — "Gemini — Faster", a
 * project's key and its name — while the chip has room for the short form.
 */
export default function CopilotPillSelect({
  label,
  display,
  value,
  onChange,
  children,
  icon,
  tone = 'neutral',
  disabled = false,
  testId,
  title,
}: CopilotPillSelectProps) {
  return (
    <span
      className={[
        'relative inline-flex h-7 items-center gap-1 rounded-lg border bg-white px-2 text-xs font-medium leading-none shadow-[0px_1px_1px_rgba(0,0,0,0.05)] dark:bg-[var(--bg-tertiary)]',
        TONE_CLASS[tone],
        disabled ? 'opacity-50' : '',
      ].join(' ')}
      title={title}
    >
      {icon}
      <span className="max-w-[9rem] truncate">{display}</span>
      <ChevronDownIcon className="h-3 w-3 shrink-0" />
      {/* Over the whole chip and invisible: the chip is what is drawn, the
          select is what is operated. `aria-label` carries the name the visible
          text does not spell out. */}
      <select
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      >
        {children}
      </select>
    </span>
  )
}
